// LoanRequestsWithTabs.jsx
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import db from "./Firebase";
import moment from "moment";
import * as XLSX from "xlsx";
import {
  useTable,
  useGlobalFilter,
  useSortBy,
  usePagination,
} from "react-table";
import SideMenu from "./SideMenu";

function GlobalFilter({ globalFilter, setGlobalFilter, preGlobalFilteredRows }) {
  const count = preGlobalFilteredRows.length;
  return (
    <input
      className="form-control mb-3"
      value={globalFilter || ""}
      onChange={e => setGlobalFilter(e.target.value || undefined)}
      placeholder={`🔍 Search ${count} records...`}
    />
  );
}

const computeStatusCategory = (loan) => {
  if (loan.repayment_completed === 1) return "Repayment Completed";
  if (loan.status === 1) return "Loan Processed";

  const docVer = Array.isArray(loan.progress)
    ? loan.progress.find(p => p.label === "Document Verification")
    : null;

  if (docVer && docVer.date && docVer.date.trim() !== "") return "Under Verification";

  return "New Application";
};

const computeStatusCategoryAll = (loan) => {
  if (loan.repayment_completed === 1) return "Repayment Completed";
  if (loan.status === 1) return "Loan Processed";

  const docVer = Array.isArray(loan.progress)
    ? loan.progress.find(p => p.label === "Document Verification")
    : null;

  if (docVer && docVer.date && docVer.date.trim() !== "") return "Under Verification";

  return "New Application";
};


const computeStatusText = (loan) => {
  const cat = computeStatusCategory(loan);

  // For New Application: Since X days
  if (cat === "New Application") {
    const created = loan.updated_date
      ? (loan.updated_date.toDate ? loan.updated_date.toDate() : new Date(loan.updated_date))
      : null;
    if (created) {
      const days = moment().startOf('day').diff(moment(created).startOf('day'), 'days');
      return `Since ${days} day${days === 1 ? "" : "s"}`;
    }
    return "Since date unknown";
  }

  // Under Verification: find the first non-complete stage (or show where stage is and its status)
  if (cat === "Under Verification") {
    if (Array.isArray(loan.progress)) {
      // find first stage that is not isComplete, or if all complete except maybe others
      for (let prog of loan.progress) {
        if (!prog.isComplete) {
          // This is current stage
          return `${prog.label} — ${prog.status || "Pending"}`;
        }
      }
      // If all stages are isComplete, but status isn't Processed, you might show last one
      const lastProg = loan.progress[loan.progress.length - 1];
      return `${lastProg.label} — ${lastProg.status || "Completed"}`;
    }
    return "Verification in progress";
  }

  // Loan Processed: show Disbursement Date if exists, else maybe updated_date
  if (cat === "Loan Processed") {
    // try to find loan_disbursement_date field or in progress
    if (loan.loan_disbursement_date) {
      const dt = loan.loan_disbursement_date;
      const disp = dt.toDate ? dt.toDate() : new Date(dt);
      return `Disbursement Date: ${moment(disp).format("DD‑MM‑YYYY")}`;
    }
    // fallback
    if (loan.updated_date) {
      const up = loan.updated_date.toDate ? loan.updated_date.toDate() : new Date(loan.updated_date);
      return `Processed on: ${moment(up).format("DD‑MM‑YYYY")}`;
    }
    return "Processed";
  }

  // Repayment Completed: show updated_date
  if (cat === "Repayment Completed") {
    if (loan.updated_date) {
      const up = loan.updated_date.toDate ? loan.updated_date.toDate() : new Date(loan.updated_date);
      return `Completed on: ${moment(up).format("DD‑MM‑YYYY")}`;
    }
    return "Repayment completed";
  }

  return "";
};

const formatExportPrimitive = (value) => {
  if (value == null) return "";
  if (value?.toDate) {
    return moment(value.toDate()).format("YYYY-MM-DD HH:mm:ss");
  }
  if (value instanceof Date) {
    return moment(value).format("YYYY-MM-DD HH:mm:ss");
  }
  return value;
};

const serializeExportValue = (value) => {
  if (value == null) return "";

  if (value?.toDate || value instanceof Date) {
    return formatExportPrimitive(value);
  }

  if (Array.isArray(value)) {
    const serializedItems = value.map(item => serializeExportValue(item));
    const hasNestedValues = serializedItems.some(
      item => item && typeof item === "object"
    );

    return hasNestedValues
      ? JSON.stringify(serializedItems)
      : serializedItems.join(", ");
  }

  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        serializeExportValue(nestedValue),
      ])
    );
  }

  return value;
};

const flattenForExcel = (value, prefix = "", output = {}) => {
  if (value == null) {
    if (prefix) output[prefix] = "";
    return output;
  }

  if (
    value?.toDate ||
    value instanceof Date ||
    Array.isArray(value) ||
    typeof value !== "object"
  ) {
    if (prefix) output[prefix] = serializeExportValue(value);
    return output;
  }

  const entries = Object.entries(value);
  if (!entries.length && prefix) {
    output[prefix] = "";
    return output;
  }

  entries.forEach(([key, nestedValue]) => {
    const nextPrefix = prefix ? `${prefix}.${key}` : key;

    if (
      nestedValue &&
      typeof nestedValue === "object" &&
      !Array.isArray(nestedValue) &&
      !nestedValue?.toDate &&
      !(nestedValue instanceof Date)
    ) {
      flattenForExcel(nestedValue, nextPrefix, output);
      return;
    }

    output[nextPrefix] = serializeExportValue(nestedValue);
  });

  return output;
};

const buildExportRow = (loan) => ({
  "Document ID": loan.id ?? "",
  "Loan ID": loan.loan_id ?? "",
  Username: loan.username ?? "",
  "Full Name": loan.fullName ?? "",
  "Loan Amount": loan.loanAmount ?? "",
  "Created Date": loan.formattedDate ?? "",
  "Status Type": loan.statusCategoryall ?? loan.statusCategory ?? "",
  Status: loan.statusText ?? "",
  ...flattenForExcel(loan),
});

const LoanRequestsWithTabs = () => {
  const [allLoans, setAllLoans] = useState([]);
  const [tab, setTab] = useState("All");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const q = query(collection(db, "nv_loan"), orderBy("created_date", "desc"));
      const snap = await getDocs(q);
      const data = snap.docs.map(docSnap => {
        const d = docSnap.data();
        return {
          ...d,
          id: docSnap.id,
          statusCategory: computeStatusCategory(d),
          statusCategoryall: computeStatusCategoryAll(d),
          statusText: computeStatusText(d),
          formattedDate: d.created_date
            ? moment(d.created_date.toDate ? d.created_date.toDate() : d.created_date).format("YYYY-MM-DD HH:mm")
            : "",
        };
      });
      setAllLoans(data);
    };
    fetchData();
  }, []);

  const filteredLoans = useMemo(() => {
    if (tab === "All") return allLoans;
    return allLoans.filter(loan => loan.statusCategory === tab);
  }, [allLoans, tab]);

  const columns = useMemo(() => [
    {
      Header: "Loan ID",
      accessor: "loan_id"
    },
    {
      Header: "Full Name",
      accessor: "fullName"
    },
    {
      Header: "Loan Amount",
      accessor: "loanAmount"
    },
    {
      Header: "Created",
      accessor: "formattedDate"
    },
    {
      Header: "Status Type",
      accessor: "statusCategoryall"
    },
    {
      Header: "Status",
      accessor: "statusText"
    },
  
    {
      Header: "Details",
      accessor: "username",
      Cell: ({ row }) => (
        <Link to={`/user_loan_details/${row.original.loan_id}`} target="_blank" rel="noopener noreferrer">🔍 View</Link>
      )
    }
  ], []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state,
    setGlobalFilter,
    preGlobalFilteredRows,
    rows,
    nextPage,
    previousPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    setPageSize,
    state: { pageIndex, pageSize }
  } = useTable(
    {
      columns,
      data: filteredLoans,
      initialState: { pageIndex: 0, pageSize: 10 }
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const handleExport = () => {
    const exportRows = rows.map(row => buildExportRow(row.original));

    if (!exportRows.length) return;

    const headers = Array.from(
      exportRows.reduce((headerSet, row) => {
        Object.keys(row).forEach(key => headerSet.add(key));
        return headerSet;
      }, new Set())
    );

    const worksheet = XLSX.utils.json_to_sheet(exportRows, { header: headers });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Loan Requests");

    const tabName = (tab || "All").replace(/[^a-z0-9]+/gi, "_");
    XLSX.writeFile(
      workbook,
      `Loan_Requests_${tabName}_${moment().format("YYYYMMDD_HHmmss")}.xlsx`
    );
  };

  const tabLabels = [
    "All",
    "New Application",
    "Under Verification",
    "Loan Processed",
    "Repayment Completed"
  ];

  return (
    <>
      <SideMenu onToggle={setSidebarOpen} />
      <div className="p-5">
        <h2 className="mb-3 bold">📋 Loan Requests</h2>

        <div className="d-flex justify-content-end mb-3">
          <button
            type="button"
            className="btn btn-success btn-sm"
            onClick={handleExport}
            disabled={rows.length === 0}
          >
            Export Excel
          </button>
        </div>

        {/* Tab Buttons */}
        <div className="mb-3 d-flex gap-2 flex-wrap">
          {tabLabels.map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`btn btn-sm ${tab === t ? "btn-primary" : "btn-outline-secondary"}`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Global Search */}
        <GlobalFilter
          globalFilter={state.globalFilter}
          setGlobalFilter={setGlobalFilter}
          preGlobalFilteredRows={preGlobalFilteredRows}
        />

        {/* Table */}
        <div className="table-responsive">
          <table {...getTableProps()} className="table table-bordered table-hover">
            <thead>
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(col => (
                    <th {...col.getHeaderProps(col.getSortByToggleProps())}>
                      {col.render("Header")}
                      {col.isSorted ? (col.isSortedDesc ? " 🔽" : " 🔼") : ""}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="text-center">No data found</td>
                </tr>
              ) : (
                page.map(row => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map(cell => (
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        

        {/* Pagination */}
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div>
            <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{"<<"}</button>{" "}
            <button onClick={() => previousPage()} disabled={!canPreviousPage}>{"<"}</button>{" "}
            <button onClick={() => nextPage()} disabled={!canNextPage}>{">"}</button>{" "}
            <button onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage}>{">>"}</button>{" "}
            <span className="ms-2">Page {pageIndex + 1} of {pageOptions.length}</span>
          </div>
          <div>
            <select
              className="form-select form-select-sm"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {[10, 20, 50].map(size => (
                <option key={size} value={size}>Show {size}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoanRequestsWithTabs;


// // LoanRequestsWithTabs.jsx
// import React, { useState, useEffect, useMemo } from "react";
// import { Link } from "react-router-dom";
// import { collection, getDocs, query, orderBy } from "firebase/firestore";
// import db from "./Firebase";
// import moment from "moment";
// import {
//   useTable,
//   useGlobalFilter,
//   useSortBy,
//   usePagination
// } from "react-table";
// import SideMenu from "./SideMenu";

// function GlobalFilter({ globalFilter, setGlobalFilter, preGlobalFilteredRows }) {
//   const count = preGlobalFilteredRows.length;
//   return (
//     <input
//       className="form-control mb-3"
//       value={globalFilter || ""}
//       onChange={e => setGlobalFilter(e.target.value || undefined)}
//       placeholder={`🔍 Search ${count} records...`}
//     />
//   );
// }

// const computeStatusCategory = (loan) => {
//   if (loan.repayment_completed === 1) return "Repayment Completed";
//   if (loan.status === 1) return "Loan Processed";

//   const docVer = Array.isArray(loan.progress)
//     ? loan.progress.find(p => p.label === "Document Verification")
//     : null;

//   if (docVer && docVer.date && docVer.date.trim() !== "") return "Under Verification";

//   return "New Application";
// };

// const LoanRequestsWithTabs = () => {
//   const [allLoans, setAllLoans] = useState([]);
//   const [tab, setTab] = useState("All");

//   useEffect(() => {
//     const fetchData = async () => {
//       const q = query(collection(db, "nv_loan"), orderBy("created_date", "desc"));
//       const snap = await getDocs(q);
//       const data = snap.docs.map(docSnap => {
//         const d = docSnap.data();
//         return {
//           ...d,
//           id: docSnap.id,
//           statusCategory: computeStatusCategory(d),
//           formattedDate: d.created_date
//             ? moment(d.created_date.toDate ? d.created_date.toDate() : d.created_date).format("YYYY-MM-DD HH:mm")
//             : "",
//         };
//       });
//       setAllLoans(data);
//     };
//     fetchData();
//   }, []);

//   const filteredLoans = useMemo(() => {
//     if (tab === "All") return allLoans;
//     return allLoans.filter(loan => loan.statusCategory === tab);
//   }, [allLoans, tab]);

//   const columns = useMemo(() => [
//     {
//       Header: "Loan ID",
//       accessor: "loan_id"
//     },
//     {
//       Header: "Full Name",
//       accessor: "fullName"
//     },
//     {
//       Header: "Loan Amount",
//       accessor: "loanAmount"
//     },
//     {
//       Header: "Created",
//       accessor: "formattedDate"
//     },
//     {
//       Header: "Status",
//       accessor: "statusCategory"
//     },
//     {
//       Header: "Details",
//       accessor: "username",
//       Cell: ({ value }) => (
//         <Link to={`/user_loan_details/${value}`} target="_blank" rel="noopener noreferrer">🔍 View</Link>
//       )
//     }
//   ], []);

//   const {
//     getTableProps,
//     getTableBodyProps,
//     headerGroups,
//     page,
//     prepareRow,
//     state,
//     setGlobalFilter,
//     preGlobalFilteredRows,
//     nextPage,
//     previousPage,
//     canPreviousPage,
//     canNextPage,
//     pageOptions,
//     gotoPage,
//     setPageSize,
//     state: { pageIndex, pageSize }
//   } = useTable(
//     {
//       columns,
//       data: filteredLoans,
//       initialState: { pageIndex: 0, pageSize: 10 }
//     },
//     useGlobalFilter,
//     useSortBy,
//     usePagination
//   );

//   const tabLabels = [
//     "All",
//     "New Application",
//     "Under Verification",
//     "Loan Processed",
//     "Repayment Completed"
//   ];

//     const [sidebarOpen, setSidebarOpen] = useState(false);

//     return (
//         <>

//             <SideMenu onToggle={setSidebarOpen} />
//     <div className="p-5">
//       <h2 className="mb-3 bold">📋 Loan Requests</h2>

//       {/* Tab Buttons */}
//       <div className="mb-3 d-flex gap-2 flex-wrap">
//         {tabLabels.map(t => (
//           <button
//             key={t}
//             onClick={() => setTab(t)}
//             className={`btn btn-sm ${tab === t ? "btn-primary" : "btn-outline-secondary"}`}
//           >
//             {t}
//           </button>
//         ))}
//       </div>

//       {/* Global Search */}
//       <GlobalFilter
//         globalFilter={state.globalFilter}
//         setGlobalFilter={setGlobalFilter}
//         preGlobalFilteredRows={preGlobalFilteredRows}
//       />

//       {/* Table */}
//       <div className="table-responsive">
//         <table {...getTableProps()} className="table table-bordered table-hover">
//           <thead>
//             {headerGroups.map(headerGroup => (
//               <tr {...headerGroup.getHeaderGroupProps()}>
//                 {headerGroup.headers.map(col => (
//                   <th {...col.getHeaderProps(col.getSortByToggleProps())}>
//                     {col.render("Header")}
//                     {col.isSorted ? (col.isSortedDesc ? " 🔽" : " 🔼") : ""}
//                   </th>
//                 ))}
//               </tr>
//             ))}
//           </thead>
//           <tbody {...getTableBodyProps()}>
//             {page.length === 0 ? (
//               <tr>
//                 <td colSpan={columns.length} className="text-center">No data found</td>
//               </tr>
//             ) : (
//               page.map(row => {
//                 prepareRow(row);
//                 return (
//                   <tr {...row.getRowProps()}>
//                     {row.cells.map(cell => (
//                       <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
//                     ))}
//                   </tr>
//                 );
//               })
//             )}
//           </tbody>
//         </table>
//       </div>

//       {/* Pagination */}
//       <div className="d-flex justify-content-between align-items-center mt-3">
//         <div>
//           <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{"<<"}</button>{" "}
//           <button onClick={() => previousPage()} disabled={!canPreviousPage}>{"<"}</button>{" "}
//           <button onClick={() => nextPage()} disabled={!canNextPage}>{">"}</button>{" "}
//           <button onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage}>{">>"}</button>{" "}
//           <span className="ms-2">Page {pageIndex + 1} of {pageOptions.length}</span>
//         </div>
//         <div>
//           <select
//             className="form-select form-select-sm"
//             value={pageSize}
//             onChange={(e) => setPageSize(Number(e.target.value))}
//           >
//             {[10, 20, , 100, 250, 500].map(size => (
//               <option key={size} value={size}>Show {size}</option>
//             ))}
//           </select>
//         </div>
//       </div>
//     </div>
//    </>
//   );
// };

// export default LoanRequestsWithTabs;


// // import React, { useState, useEffect, useMemo } from "react";
// // import { useNavigate, Link } from "react-router-dom";
// // import { collection, getDocs, orderBy, query } from "firebase/firestore";
// // import db from "./Firebase";
// // import moment from "moment";
// // import {
// //   useTable,
// //   useGlobalFilter,
// //   useSortBy,
// //   usePagination
// // } from "react-table";

// // // Global filter input
// // function GlobalFilter({ globalFilter, setGlobalFilter, preGlobalFilteredRows }) {
// //   const count = preGlobalFilteredRows.length;
// //   return (
// //     <span>
// //       <input
// //         className="form-control mb-3"
// //         value={globalFilter || ""}
// //         onChange={e => setGlobalFilter(e.target.value || undefined)}
// //         placeholder={`Search ${count} records...`}
// //       />
// //     </span>
// //   );
// // }

// // const LoanRequest = () => {
// //   const navigate = useNavigate();
// //   const [tableData, setTableData] = useState([]);

// //   useEffect(() => {
// //     // add auth check etc
// //     const fetchData = async () => {
// //       try {
// //         const q = query(collection(db, "nv_loan"), orderBy("created_date", "desc"));
// //         const snap = await getDocs(q);
// //         const data = snap.docs.map(docSnap => {
// //           const d = docSnap.data();
// //           return {
// //             id: docSnap.id,
// //             ...d,
// //             statusCategory: computeStatusCategory(d),
// //             formattedCreatedDate: d.created_date
// //               ? moment(d.created_date.toDate ? d.created_date.toDate() : d.created_date).format("YYYY-MM-DD HH:mm")
// //               : "",
// //             // other formatted fields if needed
// //           };
// //         });
// //         setTableData(data);
// //       } catch (err) {
// //         console.error("Error fetching loans:", err);
// //       }
// //     };
// //     fetchData();
// //   }, []);

// //   // Function to compute which category the loan falls into
// //   const computeStatusCategory = (d) => {
// //     // 1. Repayment Completed
// //     if (d.repayment_completed === 1) {
// //       return "Repayment Completed";
// //     }
// //     // 2. Done (Disbursed or status == final status)
// //     // For example, if you have a field like final_status or status, adapt accordingly
// //     // Here, assume status == 1 means disbursed/done
// //     if (d.status === 1) {
// //       return "Done";
// //     }
// //     // 3. Under Processing: Document Verification date exists
// //     if (Array.isArray(d.progress)) {
// //       const docVer = d.progress.find(p => p.label === "Document Verification");
// //       if (docVer && docVer.date && docVer.date.trim() !== "") {
// //         return "Under Processing";
// //       }
// //     }
// //     // 4. New Application
// //     return "New Application";
// //   };

// //   const columns = useMemo(() => [
// //     {
// //       Header: "Loan ID",
// //       accessor: "loan_id",
// //       Cell: ({ value }) => value || "-"
// //     },
// //     {
// //       Header: "Full Name",
// //       accessor: "fullName",
// //       Cell: ({ value }) => value || "-"
// //     },
// //     {
// //       Header: "Loan Amount",
// //       accessor: "loanAmount",
// //       Cell: ({ value }) => value || "-"
// //     },
// //     {
// //       Header: "Created Date",
// //       accessor: "formattedCreatedDate"
// //     },
// //     {
// //       Header: "Status",
// //       accessor: "statusCategory"
// //     },
// //     {
// //       Header: "Details",
// //       accessor: "id",  // or some other field
// //       Cell: ({ row }) => (
// //         <Link to={`/loan_details/${row.original.username}`}>
// //           View
// //         </Link>
// //       )
// //     }
// //   ], []);

// //   const {
// //     getTableProps,
// //     getTableBodyProps,
// //     headerGroups,
// //     rows,
// //     prepareRow,
// //     state,
// //     setGlobalFilter,
// //     preGlobalFilteredRows,
// //     page,
// //     canPreviousPage,
// //     canNextPage,
// //     nextPage,
// //     previousPage,
// //     pageOptions,
// //     gotoPage,
// //     state: { pageIndex, pageSize },
// //     setPageSize
// //   } = useTable(
// //     {
// //       columns,
// //       data: tableData,
// //       initialState: { pageIndex: 0, pageSize: 10 }
// //     },
// //     useGlobalFilter,
// //     useSortBy,
// //     usePagination
// //   );

// //   return (
// //     <div className="container my-4">
// //       <h2>Loan Applications</h2>
// //       <GlobalFilter
// //         preGlobalFilteredRows={preGlobalFilteredRows}
// //         globalFilter={state.globalFilter}
// //         setGlobalFilter={setGlobalFilter}
// //       />
// //       <table {...getTableProps()} className="table table-bordered table-hover">
// //         <thead>
// //           {headerGroups.map(headerGroup => (
// //             <tr {...headerGroup.getHeaderGroupProps()}>
// //               {headerGroup.headers.map(col => (
// //                 <th {...col.getHeaderProps(col.getSortByToggleProps())}>
// //                   {col.render("Header")}
// //                   {col.isSorted ? (col.isSortedDesc ? " 🔽" : " 🔼") : ""}
// //                 </th>
// //               ))}
// //             </tr>
// //           ))}
// //         </thead>
// //         <tbody {...getTableBodyProps()}>
// //           {page.map(row => {
// //             prepareRow(row);
// //             return (
// //               <tr {...row.getRowProps()}>
// //                 {row.cells.map(cell => (
// //                   <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
// //                 ))}
// //               </tr>
// //             );
// //           })}
// //         </tbody>
// //       </table>
// //       <div className="pagination-controls">
// //         <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{"<<"}</button>{" "}
// //         <button onClick={() => previousPage()} disabled={!canPreviousPage}>{"<"}</button>{" "}
// //         <button onClick={() => nextPage()} disabled={!canNextPage}>{">"}</button>{" "}
// //         <button onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage}>{">>"}</button>{" "}
// //         <span>Page {pageIndex + 1} of {pageOptions.length}</span>{" "}
// //         <select
// //           value={pageSize}
// //           onChange={e => setPageSize(Number(e.target.value))}
// //         >
// //           {[10, 20, 50].map(size => (
// //             <option key={size} value={size}>Show {size}</option>
// //           ))}
// //         </select>
// //       </div>
// //     </div>
// //   );
// // };

// // export default LoanRequest;


// // // // import React, { useState, useEffect } from 'react';

// // // // import { useNavigate, Link, useParams } from 'react-router-dom';
// // // // import db from "./Firebase";
// // // // import { arrayUnion } from "firebase/firestore";
// // // // import { storage } from './Firebase';
// // // // import { ref, getDownloadURL, uploadBytesResumable } from "firebase/storage";
// // // // import Moment from 'moment';
// // // // import { extendMoment } from 'moment-range';
// // // // import { DateRangePicker } from 'react-date-range'
// // // // import { CSVLink } from "react-csv";
// // // // import 'react-date-range/dist/styles.css'; // main style file
// // // // import 'react-date-range/dist/theme/default.css';
// // // // import { DateRange } from 'react-date-range';
// // // // import ReactFlexyTable from 'react-flexy-table'
// // // // import 'react-flexy-table/dist/index.css';

// // // // function LoanRequest() {

// // // //     const data = [
// // // //         { name: 'John', age: 25, city: 'New York', name2: 'John', age2: 25, city2: 'New York' },
// // // //         { name: 'Mary', age: 30, city: 'Los Angeles', name2: 'John', age2: 25, city2: 'New York' },
// // // //     ];

// // // //     const columns = [
// // // //         { header: 'Name', key: 'name' },
// // // //         { header: 'Age', key: 'age' },
// // // //         { header: 'City', key: 'city' },
// // // //     ];

// // // //     const additionalCols = [
// // // //         {
// // // //             header: 'Actions',
// // // //             td: (data) => {
// // // //                 return (
// // // //                     <div>
// // // //                         <img
// // // //                             src="{deleteIcon}"
// // // //                             width='30'
// // // //                             height='20'
// // // //                             onClick={() => alert('this is delete for id ' + data.id)}
// // // //                         />{' '}
// // // //                 // delete icon
// // // //                         <img
// // // //                             src="{editIcon}"
// // // //                             width='30'
// // // //                             height='20'
// // // //                             onClick={() => alert('this is edit for id ' + data.id)}
// // // //                         /> // edit icon
// // // //                     </div>
// // // //                 )
// // // //             }
// // // //         }
// // // //     ]


// // // //     return (
// // // //         <>
// // // //             <div>
// // // //                 <ReactFlexyTable data={data} filterable globalSearch className='mytabo' />
// // // //             </div>
// // // //         </>
// // // //     )
// // // // }

// // // // export default LoanRequest;

// // // import React, { useState, useEffect } from 'react';

// // // import { useNavigate, Link, useParams } from 'react-router-dom';
// // // import db from "./Firebase";
// // // import firebaseApp from "./Firebase";
// // // import { arrayUnion } from "firebase/firestore";
// // // import { storage } from './Firebase';
// // // import { ref, getDownloadURL, uploadBytesResumable } from "firebase/storage";
// // // import Moment from 'moment';
// // // import { extendMoment } from 'moment-range';
// // // import ViewAllRequests from './ViewAllRequests';
// // // import ViewAppData from './ViewAppData';

// // // import { useTable, useSortBy, useFilters, useGlobalFilter, useAsyncDebounce, usePagination } from "react-table";
// // // import { CSVLink } from "react-csv";
// // // import 'react-date-range/dist/styles.css'; // main style file
// // // import 'react-date-range/dist/theme/default.css';
// // // import { DateRange } from 'react-date-range';

// // // function GlobalFilter({
// // //     preGlobalFilteredRows,
// // //     globalFilter,
// // //     setGlobalFilter,
// // // }) {
// // //     const temp = preGlobalFilteredRows.length;
// // //     const [value, setValue] = React.useState(globalFilter);
// // //     const onChange = useAsyncDebounce((value) => {
// // //         setGlobalFilter(value || undefined);
// // //     }, 200);

// // //     return (
// // //         <span>
// // //             {/* Search:{" "} */}
// // //             <input
// // //                 class="form-control"
// // //                 value={value || ""}
// // //                 onChange={(e) => {
// // //                     setValue(e.target.value);
// // //                     onChange(e.target.value);
// // //                 }}
// // //                 placeholder={`Search ${temp} records...`}

// // //             />
// // //         </span>
// // //     );
// // // }

// // // // Define a default UI for filtering
// // // function DefaultColumnFilter({
// // //     column: { filterValue, preFilteredRows, setFilter },
// // // }) {
// // //     const temp = preFilteredRows.length;

// // //     return (
// // //         <input class="form-control" style={{ marginTop: "11px" }} id="email" onChange={(e) => {
// // //             setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
// // //         }} value={filterValue || ""} placeholder="Search" />
// // //         // <input
// // //         //     value={filterValue || ""}
// // //         //     onChange={(e) => {
// // //         //         setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
// // //         //     }}
// // //         //     placeholder={`Search ${temp} records...`}
// // //         // />
// // //     );
// // // }


// // // function LoanRequest() {
// // //     const navigate = useNavigate();
// // //     const moment = extendMoment(Moment);
// // //     const [tableData, setTableData] = useState([]);
// // //     const [data, setData] = useState([]);
// // //     const [pdata, setPData] = useState({});
// // //     const [csvdata, setCsvdata] = useState([]);

// // //     useEffect(() => {
// // //         const storedUsername = localStorage.getItem("email");
// // //         const storedPassword = localStorage.getItem("password");
// // //         const storedRemember = localStorage.getItem("remember");

// // //         if (storedRemember === "false" || !storedUsername || !storedPassword) {
// // //             navigate('/signin');
// // //         }
// // //     }, []);

// // //     const headers = [
// // //         { label: "Applicant Name", key: "applicant_name" },
// // //         { label: "Applicant Name", key: "applicant_phone" },
// // //         { label: "loan_id", key: "loan_id" },
// // //         { label: "applicant_date_of_birth", key: "applicant_date_of_birth" },
// // //         { label: "loan_type", key: "loan_type" },
// // //         { label: "gender", key: "gender" },
// // //         { label: "applicant_alternate_phone", key: "applicant_alternate_phone" },
// // //         { label: "applicant_marital_status", key: "applicant_marital_status" },
// // //         { label: "fathers_name", key: "fathers_name" },
// // //         { label: "mothers_name", key: "mothers_name" },
// // //         { label: "city", key: "city" },
// // //         { label: "state", key: "state" },
// // //         { label: "pincode", key: "pincode" },
// // //         { label: "full_address", key: "full_address" },
// // //         { label: "highest_education", key: "highest_education" },
// // //         { label: "business_type", key: "business_type" },
// // //         { label: "parlour_freelancer_name", key: "parlour_freelancer_name" },
// // //         { label: "parlour_address", key: "parlour_address" },
// // //         { label: "ownership_type", key: "ownership_type" },
// // //         { label: "working_since", key: "working_since" },
// // //         { label: "monthly_since", key: "monthly_since" },
// // //         { label: "bank_account_number", key: "bank_account_number" },
// // //         { label: "bank_name", key: "bank_name" },
// // //         { label: "ifsc_code", key: "ifsc_code" },
// // //         { label: "upi_id", key: "upi_id" },
// // //         { label: "loan_amount", key: "loan_amount" },
// // //         { label: "have_you_taken_loan_in_past", key: "have_you_taken_loan_in_past" },
// // //         { label: "needGrant", key: "needGrant" },
// // //         { label: "purposeOfReason", key: "purposeOfReason" },
// // //         { label: "reasonEstimated", key: "reasonEstimated" },
// // //         { label: "repaymentModel", key: "repaymentModel" },
// // //         { label: "how_did_you_know_about_scheme", key: "how_did_you_know_about_scheme" },
// // //         { label: "aadhar_front", key: "aadhar_front" },
// // //         { label: "aadhar_back", key: "aadhar_back" },
// // //         { label: "pan", key: "pan" },
// // //         { label: "passbook", key: "passbook" },
// // //         { label: "parlour_photo", key: "parlour_photo" },
// // //         { label: "applicant_photo", key: "applicant_photo" },
// // //         { label: "error_message", key: "error_message" },
// // //         { label: "document_status", key: "document_status" },
// // //         { label: "document_status_done_msg", key: "document_status_done_msg" },
// // //         { label: "document_status_pending_msg", key: "document_status_pending_msg" },
// // //         { label: "verification_status", key: "verification_status" },
// // //         { label: "verification_status_done_msg", key: "verification_status_done_msg" },
// // //         { label: "verification_status_pending_msg", key: "verification_status_pending_msg" },
// // //         { label: "nbfc_status", key: "nbfc_status" },
// // //         { label: "nbfc_status_done_msg", key: "nbfc_status_done_msg" },
// // //         { label: "nbfc_status_pending_msg", key: "nbfc_status_pending_msg" },
// // //         { label: "penny_check_status", key: "penny_check_status" },
// // //         { label: "penny_check_status_done_msg", key: "penny_check_status_done_msg" },
// // //         { label: "penny_check_status_pending_msg", key: "penny_check_status_pending_msg" },
// // //         { label: "godrej_status", key: "godrej_status" },
// // //         { label: "godrej_status_done_msg", key: "godrej_status_done_msg" },
// // //         { label: "godrej_status_pending_msg", key: "godrej_status_pending_msg" },
// // //         { label: "loan_disbursement_status", key: "loan_disbursement_status" },
// // //         { label: "loan_disbursement_status_done_msg", key: "loan_disbursement_status_done_msg" },
// // //         { label: "loan_disbursement_status_pending_msg", key: "loan_disbursement_status_pending_msg" },
// // //         { label: "custom_msg_one", key: "custom_msg_one" },
// // //         { label: "custom_msg_two", key: "custom_msg_two" },
// // //         { label: "status", key: "status" },
// // //         { label: "created_date", key: "created_date", dataFormat: "MM/DD/YY, h:mm a" },
// // //         { label: "updated_date", key: "updated_date", dataFormat: "MM/DD/YY, h:mm a" },
// // //     ];

// // //     const changeStatus = () => {
// // //         openModalShow();
// // //     }

// // //     const openModalShow = () => {
// // //         // alert("ooo")
// // //         window.openModelshow();
// // //     }

// // //     const closeModel = () => {
// // //         window.openModelNew();
// // //     }

// // //     const LoanProcessed = async () => {
// // //         const snapshot = await db.collection("loan").where("status", "==", 1).orderBy("created_date", "desc").get();
// // //         const data = snapshot.docs.map((doc) => ({
// // //             ...doc.data(),
// // //             id: doc.id,
// // //         }));

// // //         setCsvdata(
// // //             snapshot.docs.map((doc) => {
// // //                 const data = doc.data();
// // //                 return {
// // //                     ...data,
// // //                     created_date: moment(data.created_date.toDate().toISOString()).format("MM/DD/YY, h:mm a"),
// // //                     updated_date: moment(data.updated_date.toDate().toISOString()).format("MM/DD/YY, h:mm a"),
// // //                 };
// // //             })
// // //         );

// // //         setTableData(data);
// // //         // LoanProcessed();
// // //     };

// // //     const LoanUnderProcess = async () => {
// // //         const snapshot = await db.collection("loan").where("status", "==", 0).orderBy("created_date", "desc").get();
// // //         const data = snapshot.docs.map((doc) => ({
// // //             ...doc.data(),
// // //             id: doc.id,
// // //         }));
// // //         setCsvdata(
// // //             snapshot.docs.map((doc) => {
// // //                 const data = doc.data();
// // //                 return {
// // //                     ...data,
// // //                     created_date: moment(data.created_date.toDate().toISOString()).format("MM/DD/YY, h:mm a"),
// // //                     updated_date: moment(data.updated_date.toDate().toISOString()).format("MM/DD/YY, h:mm a"),
// // //                 };
// // //             })
// // //         );

// // //         setTableData(data);
// // //         // LoanUnderProcess();
// // //     };

// // //     const LoanRejected = async () => {
// // //         const snapshot = await db.collection("loan").where("status", "==", 2).orderBy("created_date", "desc").get();
// // //         const data = snapshot.docs.map((doc) => ({
// // //             ...doc.data(),
// // //             id: doc.id,
// // //         }));

// // //         setTableData(data);
// // //         setCsvdata(
// // //             snapshot.docs.map((doc) => {
// // //                 const data = doc.data();
// // //                 return {
// // //                     ...data,
// // //                     created_date: moment(data.created_date.toDate().toISOString()).format("MM/DD/YY, h:mm a"),
// // //                     updated_date: moment(data.updated_date.toDate().toISOString()).format("MM/DD/YY, h:mm a"),
// // //                 };
// // //             })
// // //         );
// // //         // LoanUnderProcess();
// // //     };

// // //     const LoanRepaymentCompleted = async () => {
// // //         const snapshot = await db.collection("loan").where("repayment_completed", "==", 1).orderBy("updated_date", "desc").get();
// // //         const data = snapshot.docs.map((doc) => ({
// // //             ...doc.data(),
// // //             id: doc.id,
// // //         }));

// // //         setTableData(data);
// // //         setCsvdata(
// // //             snapshot.docs.map((doc) => {
// // //                 const data = doc.data();
// // //                 return {
// // //                     ...data,
// // //                     created_date: moment(data.created_date.toDate().toISOString()).format("MM/DD/YY, h:mm a"),
// // //                     updated_date: moment(data.updated_date.toDate().toISOString()).format("MM/DD/YY, h:mm a"),
// // //                 };
// // //             })
// // //         );
// // //         // LoanUnderProcess();
// // //     };

// // //     const uniqueData = [];

// // //     const LoanRepaymentStarted = async () => {
// // //         const snapshot = await db.collection("repayment_model").where("status", "==", "1").get();
// // //         const data = snapshot.docs.map((doc) => ({
// // //             ...doc.data(),
// // //             id: doc.id,
// // //         }));

// // //         // Create a new Set to store unique loan_id values
// // //         const uniqueLoanIds = new Set();

// // //         // Iterate over the data array
// // //         data.forEach((item) => {
// // //             uniqueLoanIds.add(item.loan_id);
// // //         });

// // //         // Convert the Set back to an array
// // //         const uniqueData = Array.from(uniqueLoanIds);

// // //         console.log(uniqueData);

// // //         const snapshot1 = await db.collection("loan").orderBy("created_date", "desc").get();
// // //         const data1 = snapshot1.docs.map((doc) => ({
// // //             ...doc.data(),
// // //             id: doc.id,
// // //         }));

// // //         // Filter the data based on the loan_id values present in the uniqueData array
// // //         const filteredData = data1.filter((item) => uniqueData.includes(item.loan_id));
// // //         setTableData(filteredData);
// // //         // console.log(filteredData);
// // //     };


// // //     const LoanRepaymentProcess = async () => {
// // //         const snapshot = await db.collection("loan").where("status", "==", 0).orderBy("created_date", "desc").get();
// // //         const data = snapshot.docs.map((doc) => ({
// // //             ...doc.data(),
// // //             id: doc.id,
// // //         }));

// // //         // Filter the data based on the loan_id values present in the uniqueData array
// // //         const filteredData = data.filter((item) => uniqueData.includes(item.loan_id));

// // //         setTableData(filteredData);
// // //     };


// // //     const allData = async () => {
// // //         const snapshot = await db.collection("loan").orderBy("created_date", "desc").get();
// // //         const data = snapshot.docs.map((doc) => ({
// // //             ...doc.data(),
// // //             id: doc.id,
// // //         }));

// // //         setTableData(data);

// // //     };





// // //     useEffect(() => {
// // //         const fetchData = async () => {
// // //             const snapshot = await db.collection("nv_loan").orderBy("created_date", "desc").get();
// // //             const data = snapshot.docs.map((doc) => ({
// // //                 ...doc.data(),
// // //                 id: doc.id,
// // //             }));

// // //             setTableData(data);

// // //         };
// // //         fetchData();

// // //         db.collection("loan").orderBy("created_date", "desc").onSnapshot((snapshot) => {
// // //             setCsvdata(
// // //                 snapshot.docs.map((doc) => {
// // //                     const data = doc.data();
// // //                     return {
// // //                         ...data,
// // //                         created_date: moment(data.created_date.toDate().toISOString()).format("MM/DD/YY, h:mm a"),
// // //                         updated_date: moment(data.updated_date.toDate().toISOString()).format("MM/DD/YY, h:mm a"),
// // //                     };
// // //                 })
// // //             );
// // //         });
// // //     }, []);
// // //     // useEffect(() => {
// // //     //     db.collection("portfolio_details").orderBy("created_date", "desc").onSnapshot((snapshot) => {

// // //     //         setData(
// // //     //             snapshot.docs.map((doc) => ({
// // //     //                 id: doc.id,
// // //     //                 data: doc.data(),
// // //     //             }))
// // //     //         );
// // //     //     });

// // //     // }, []);

// // //     const columns = React.useMemo(
// // //         () => [

// // //             {
// // //                 Header: "Applicant Name",
// // //                 accessor: "fullName", // accessor is the "key" in the data
// // //             },
// // //             {
// // //                 Header: "Created Date",
// // //                 accessor: "created_date",
// // //                 Cell: ({ value }) => (
// // //                     moment(value.toDate().toISOString()).format("MM/DD/YY, h:mm a")
// // //                 ),
// // //             },

// // //             {
// // //                 Header: "Phone No",
// // //                 accessor: "username", // accessor is the "key" in the data
// // //             },
// // //             {
// // //                 Header: "Loan ID",
// // //                 accessor: "loan_id", // accessor is the "key" in the data
// // //             },
// // //             {
// // //                 Header: "Loan Amount",
// // //                 accessor: "loanAmount", // accessor is the "key" in the data
// // //             },

// // //             {
// // //                 Header: "Bank Name",
// // //                 accessor: "bankName", // accessor is the "key" in the data
// // //             },

// // //             {
// // //                 Header: "City Name",
// // //                 accessor: "city", // accessor is the "key" in the data
// // //             },

// // //             {
// // //                 Header: "Username",
// // //                 accessor: "uid", // accessor is the "key" in the data
// // //                 Cell: ({ row }) => (
// // //                     <Link to={`/user_loan_details/${row.original.username}`} ><h6 class="mb-0 text-xs">View</h6></Link>
// // //                     // <a href={row.original.username} target="_blank" rel="noopener noreferrer">
// // //                     //     {row.original.username}
// // //                     // </a>
// // //                 ),
// // //             },

// // //             // {
// // //             //     Header: "Created Date",
// // //             //     accessor: "created_date", // accessor is the "key" in the data
// // //             // },

// // //         ],
// // //         []
// // //     );

// // //     const defaultColumn = React.useMemo(
// // //         () => ({
// // //             // Let's set up our default Filter UI
// // //             Filter: DefaultColumnFilter,
// // //         }),
// // //         []
// // //     );

// // //     const {
// // //         getTableProps,
// // //         getTableBodyProps,
// // //         headerGroups,
// // //         rows,
// // //         prepareRow,
// // //         state,
// // //         visibleColumns,
// // //         preGlobalFilteredRows,
// // //         setGlobalFilter,
// // //         page,
// // //         state: { pageIndex, pageSize },
// // //         setPageSize,
// // //         gotoPage,
// // //         nextPage,
// // //         previousPage,
// // //         canPreviousPage,
// // //         canNextPage,
// // //         pageOptions,
// // //         pageCount,
// // //     } = useTable(
// // //         {
// // //             columns,
// // //             data: tableData,
// // //             defaultColumn, // Be sure to pass the defaultColumn option
// // //         },
// // //         useFilters,
// // //         useGlobalFilter,
// // //         useSortBy,
// // //         usePagination
// // //     );

// // //     return (
// // //         <>
// // //             {/* <ViewAllRequests /> */}
// // //             <main class=" position-relative max-height-vh-100 h-100 border-radius-lg ">
// // //                 <div class="p-2" style={{ textAlign: "start", paddingBottom: "5px" }}>
// // //                     {/* <span class="btn bg-gradient-secondary btn-sm mb-0 mr-2" style={{ color: "white" }} >&nbsp; Download Excel</span> */}
// // //                     <Link to="/"><img src="/images/menubutton.png" class="menu-toggle" alt="Responsive image" style={{ height: "2rem" }} /></Link>

// // //                 </div>
// // //                 <div class="row">


// // //                     <div class="mt-4 p-4">
// // //                         <div class="card-footer p-1" style={{ borderStyle: "none" }}>
// // //                             <div class="row">
// // //                                 <div class="pr-4" style={{ display: "flex", justifyContent: "flex-end" }}>
// // //                                     <span class="badge badge-warning dropdown-toggle" data-toggle="dropdown" id="dropdownMenuButton" aria-haspopup="true" aria-expanded="false" style={{ cursor: "pointer", backgroundColor: "#ec407a", color: "white", padding: "9px", marginRight: "1rem" }}>Services</span>
// // //                                     <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
// // //                                         <span class="dropdown-item" onClick={() => LoanUnderProcess()} style={{ cursor: "pointer" }}>Under review</span>
// // //                                         <span class="dropdown-item" onClick={() => LoanProcessed()}>Loan Processed</span>
// // //                                         <span class="dropdown-item" onClick={() => LoanRejected()}>Loan Rejected</span>
// // //                                         <span class="dropdown-item" onClick={() => LoanRepaymentStarted()}>Repayment Started</span>
// // //                                         <span class="dropdown-item" onClick={() => LoanRepaymentCompleted()}>Repayment Completed</span>
// // //                                         <span class="dropdown-item" onClick={() => allData()}>All</span>
// // //                                     </div>
// // //                                     <CSVLink data={csvdata} headers={headers}>
// // //                                         <span class="btn bg-gradient-secondary btn-sm mb-0 mr-2" style={{ color: "white" }} >&nbsp; Download Excel</span>
// // //                                     </CSVLink>
// // //                                 </div>
// // //                             </div>


// // //                         </div>


// // //                         {/* <div style={{ textAlign: "end", paddingBottom: "5px" }}>
// // //                             <span class="btn bg-gradient-secondary btn-sm mb-0 mr-2" style={{ color: "white" }} onClick={() => LoanUnderProcess()}  >&nbsp; Under review</span>
// // //                             <span class="btn bg-gradient-secondary btn-sm mb-0 mr-2" style={{ color: "white" }} onClick={() => LoanProcessed()} >&nbsp; Loan Processed</span>
// // //                             <span class="btn bg-gradient-secondary btn-sm mb-0 mr-2" style={{ color: "white" }} onClick={() => LoanRejected()} >&nbsp; Rejected</span>
// // //                             <span class="btn bg-gradient-secondary btn-sm mb-0 mr-2" style={{ color: "white" }} onClick={() => LoanRepaymentStarted()} >&nbsp; Repayment Started</span>
// // //                             <span class="btn bg-gradient-secondary btn-sm mb-0 mr-2" style={{ color: "white" }} onClick={() => LoanRepaymentCompleted()} >&nbsp; Repayment Completed</span>
// // //                             <CSVLink data={csvdata} headers={headers}>
// // //                                 <span class="btn bg-gradient-secondary btn-sm mb-0 mr-2" style={{ color: "white" }} >&nbsp; Download Excel</span>
// // //                             </CSVLink>

// // //                         </div> */}
// // //                         <div class="card">
// // //                             <div class="card-header pb-0 px-3">
// // //                                 <h6 class="mb-2">Loan Details</h6>
// // //                             </div>
// // //                             <div class="">
// // //                                 <div class="card">
// // //                                     <div class="table-responsive fix-width scroll-inner">
// // //                                         <table {...getTableProps()} class="table align-items-center mb-0">

// // //                                             <thead>
// // //                                                 {headerGroups.map((headerGroup) => (
// // //                                                     <tr {...headerGroup.getHeaderGroupProps()}>
// // //                                                         {headerGroup.headers.map((column) => (
// // //                                                             <th
// // //                                                                 {...column.getHeaderProps(column.getSortByToggleProps())}
// // //                                                                 style={{
// // //                                                                     textAlign: "left",
// // //                                                                     borderBottom: "solid 1px black",
// // //                                                                     color: "black",
// // //                                                                 }}
// // //                                                                 class="text-uppercase text-xxs font-weight-bolder"
// // //                                                             >
// // //                                                                 {column.render("Header")}
// // //                                                                 <span>
// // //                                                                     {column.isSorted ? (column.isSortedDesc ? "🔽" : "🔼") : ""}
// // //                                                                 </span>
// // //                                                                 <div>{column.canFilter ? column.render("Filter") : null}</div>
// // //                                                             </th>
// // //                                                         ))}
// // //                                                     </tr>
// // //                                                 ))}
// // //                                                 <tr>
// // //                                                     <th
// // //                                                         colSpan={visibleColumns.length}
// // //                                                         style={{
// // //                                                             textAlign: "left",
// // //                                                         }}
// // //                                                     >
// // //                                                         <GlobalFilter
// // //                                                             preGlobalFilteredRows={preGlobalFilteredRows}
// // //                                                             globalFilter={state.globalFilter}
// // //                                                             setGlobalFilter={setGlobalFilter}
// // //                                                         />
// // //                                                     </th>
// // //                                                 </tr>
// // //                                             </thead>
// // //                                             <tbody {...getTableBodyProps()} style={{ textAlign: "start" }}>
// // //                                                 {page.map((row, i) => {
// // //                                                     prepareRow(row);
// // //                                                     return (
// // //                                                         <tr {...row.getRowProps()}>
// // //                                                             {row.cells.map((cell) => {
// // //                                                                 return (
// // //                                                                     <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
// // //                                                                 );
// // //                                                             })}
// // //                                                         </tr>
// // //                                                     );
// // //                                                 })}


// // //                                             </tbody>


// // //                                         </table>
// // //                                         <div className="" style={{ paddingTop: "2rem" }}>
// // //                                             <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
// // //                                                 {"<<"}
// // //                                             </button>{" "}
// // //                                             <button onClick={() => previousPage()} disabled={!canPreviousPage}>
// // //                                                 {"<"}
// // //                                             </button>{" "}
// // //                                             <button onClick={() => nextPage()} disabled={!canNextPage}>
// // //                                                 {">"}
// // //                                             </button>{" "}
// // //                                             <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
// // //                                                 {">>"}
// // //                                             </button>{" "}
// // //                                             <span>
// // //                                                 Page{" "}
// // //                                                 <strong>
// // //                                                     {pageIndex + 1} of {pageOptions.length}
// // //                                                 </strong>{" "}
// // //                                             </span>
// // //                                             <span>
// // //                                                 | Go to page:{" "}
// // //                                                 <input
// // //                                                     type="number"
// // //                                                     defaultValue={pageIndex + 1}
// // //                                                     onChange={(e) => {
// // //                                                         const page = e.target.value ? Number(e.target.value) - 1 : 0;
// // //                                                         gotoPage(page);
// // //                                                     }}
// // //                                                     style={{ width: "100px" }}
// // //                                                 />
// // //                                             </span>{" "}
// // //                                             <select
// // //                                                 value={pageSize}
// // //                                                 onChange={(e) => {
// // //                                                     setPageSize(Number(e.target.value));
// // //                                                 }}
// // //                                             >
// // //                                                 {[10, 20, 30, 40, 50].map((pageSize) => (
// // //                                                     <option key={pageSize} value={pageSize}>
// // //                                                         Show {pageSize}
// // //                                                     </option>
// // //                                                 ))}
// // //                                             </select>
// // //                                         </div>
// // //                                     </div>
// // //                                 </div>
// // //                                 {/* <div class="card">
// // //                     <div class="table-responsive fix-width scroll-inner">
// // //                         <table class="table align-items-center mb-0 table-bordered">
// // //                             <thead>
// // //                                 <tr>
// // //                                     <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Name</th>
// // //                                     <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Title/Designation</th>
// // //                                     <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">City</th>
// // //                                     <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">State</th>
// // //                                     <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Phone No</th>
// // //                                     <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">User Name/Portfolio</th>
// // //                                     <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Created Date</th>
// // //                                 </tr>
// // //                             </thead>
// // //                             <tbody>
// // //                                 {data?.map(({ id, data }, index) => (
// // //                                     <>
// // //                                         <tr>
// // //                                             <td>
// // //                                                 <div class="d-flex px-2 py-1">
// // //                                                     <div>
// // //                                                         <img src={data.portfolio_image} class="avatar avatar-sm me-3" />
// // //                                                     </div>
// // //                                                     <div class="d-flex flex-column justify-content-center">
// // //                                                         <h6 class="mb-0 font-weight-bold text-xs" style={{ cursor: "pointer", color: "#17a2b8" }}>{data.first_name}  {data.last_name}</h6>
// // //                                                     </div>
// // //                                                 </div>
// // //                                             </td>
// // //                                             <td>
// // //                                                 <p class="text-xs mb-0">{data.title_designation}</p>
// // //                                               </td>
// // //                                             <td>
// // //                                                 <p class="text-xs mb-0">{data.city}</p>
// // //                                                </td>
// // //                                             <td>
// // //                                                 <p class="text-xs mb-0">{data.state}</p>
// // //                                                </td>
// // //                                             <td>
// // //                                                 <p class="text-xs mb-0">{data.phone_no}</p>
// // //                                               </td>
                                           

// // //                                             <td>
// // //                                                 <div class="d-flex px-2 py-1">
// // //                                                     <div class="d-flex flex-column justify-content-center">
// // //                                                         <a href={data.username} style={{ color: "blue" }} target="_blank" class="mb-0 text-xs">{data.username}</a>
// // //                                                     </div>
// // //                                                 </div>
// // //                                             </td>

// // //                                             <td>
// // //                                                 <div class="d-flex px-2 py-1">
// // //                                                     <div class="d-flex flex-column justify-content-center">
// // //                                                         <h6 class="mb-0 text-xs">{moment(data.created_date.toDate().toISOString()).format("MM/DD/YY, h:mm a")}</h6>
// // //                                                     </div>
// // //                                                 </div>
// // //                                             </td>

// // //                                             <td>
// // //                                                 <div class="d-flex px-2 py-1">
// // //                                                     <div class="d-flex flex-column justify-content-center">
// // //                                                         <h6 class="mb-0 text-xs">{data.upi_address}</h6>
// // //                                                     </div>
// // //                                                 </div>
// // //                                             </td>
// // //                                         </tr>

// // //                                     </>
// // //                                 ))}


// // //                             </tbody>
// // //                         </table>
// // //                     </div>
// // //                 </div> */}
// // //                             </div>
// // //                         </div>
// // //                     </div>

// // //                 </div>

// // //             </main>
// // //         </>
// // //     );
// // // }

// // // export default LoanRequest;












































// // // // import { useTable, useSortBy, useFilters, useGlobalFilter, useAsyncDebounce, } from "react-table";
// // // // import React, { useState, useEffect } from 'react';

// // // // import { useNavigate, Link, useParams } from 'react-router-dom';
// // // // import db from "./Firebase";
// // // // import { arrayUnion } from "firebase/firestore";
// // // // import { storage } from './Firebase';
// // // // import { ref, getDownloadURL, uploadBytesResumable } from "firebase/storage";
// // // // import Moment from 'moment';
// // // // import { extendMoment } from 'moment-range';
// // // // import { DateRangePicker } from 'react-date-range'
// // // // import { CSVLink } from "react-csv";
// // // // import 'react-date-range/dist/styles.css'; // main style file
// // // // import 'react-date-range/dist/theme/default.css';
// // // // import { DateRange } from 'react-date-range';


// // // // function GlobalFilter({
// // // //     preGlobalFilteredRows,
// // // //     globalFilter,
// // // //     setGlobalFilter,
// // // // }) {
// // // //     const temp = preGlobalFilteredRows.length;
// // // //     const [value, setValue] = React.useState(globalFilter);
// // // //     const onChange = useAsyncDebounce((value) => {
// // // //         setGlobalFilter(value || undefined);
// // // //     }, 200);

// // // //     return (
// // // //         <span>
// // // //             Search:{" "}
// // // //             <input
// // // //                 value={value || ""}
// // // //                 onChange={(e) => {
// // // //                     setValue(e.target.value);
// // // //                     onChange(e.target.value);
// // // //                 }}
// // // //                 placeholder={`${temp} records...`}
// // // //                 style={{
// // // //                     fontSize: "1.1rem",
// // // //                     border: "0",
// // // //                 }}
// // // //             />
// // // //         </span>
// // // //     );
// // // // }

// // // // // Define a default UI for filtering
// // // // function DefaultColumnFilter({
// // // //     column: { filterValue, preFilteredRows, setFilter },
// // // // }) {
// // // //     const temp = preFilteredRows.length;

// // // //     return (
// // // //         <input
// // // //             value={filterValue || ""}
// // // //             onChange={(e) => {
// // // //                 setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
// // // //             }}
// // // //             placeholder={`Search ${temp} records...`}
// // // //         />
// // // //     );
// // // // }

// // // // function LoanRequest() {
// // // //     const [tableData, setTableData] = useState([]);

// // // //     useEffect(() => {
// // // //         // db.collection("loan").orderBy("created_date", "desc").onSnapshot((snapshot) => {
// // // //         //     setData(
// // // //         //         snapshot.docs.map((doc) => ({
// // // //         //             id: doc.id,
// // // //         //             data: doc.data(),
// // // //         //         }))
// // // //         //     );
// // // //         // });

// // // //         const fetchData = async () => {
// // // //             const snapshot = await db.collection("portfolio_details").get();
// // // //             const data = snapshot.docs.map((doc) => ({
// // // //                 ...doc.data(),
// // // //                 id: doc.id,
// // // //             }));
// // // //             setTableData(data);
// // // //         };
// // // //         fetchData();
// // // //     }, []);

// // // //     const columns = React.useMemo(
// // // //         () => [
// // // //             {
// // // //                 Header: "Name",
// // // //                 accessor: "full_name",
// // // //             },
// // // //             {
// // // //                 Header: "Age",
// // // //                 accessor: "city",
// // // //             },
// // // //             {
// // // //                 Header: "Gender",
// // // //                 accessor: "parlour_type",
// // // //             },
// // // //         ],
// // // //         []
// // // //     );

// // // //     // const tableInstance = useTable({ columns, data: tableData }, useFilters,
// // // //     //     useGlobalFilter,
// // // //     //     useSortBy);

// // // //     const defaultColumn = React.useMemo(
// // // //         () => ({
// // // //             // Let's set up our default Filter UI
// // // //             Filter: DefaultColumnFilter,
// // // //         }),
// // // //         []
// // // //     );

// // // //     const {
// // // //         getTableProps,
// // // //         getTableBodyProps,
// // // //         headerGroups,
// // // //         rows,
// // // //         prepareRow,
// // // //         state,
// // // //         visibleColumns,
// // // //         preGlobalFilteredRows,
// // // //         setGlobalFilter,
// // // //     } = useTable(
// // // //         {
// // // //             columns,
// // // //             data: tableData,
// // // //             defaultColumn, // Be sure to pass the defaultColumn option
// // // //         },
// // // //         useFilters,
// // // //         useGlobalFilter,
// // // //         useSortBy
// // // //     );

// // // //     return (
// // // //         <table {...getTableProps()}>
// // // //             <thead>
// // // //                 {headerGroups.map((headerGroup) => (
// // // //                     <tr {...headerGroup.getHeaderGroupProps()}>
// // // //                         {headerGroup.headers.map((column) => (
// // // //                             <th {...column.getHeaderProps()}>{column.render("Header")}</th>
// // // //                         ))}
// // // //                     </tr>
// // // //                 ))}
// // // //             </thead>
// // // //             <tbody {...getTableBodyProps()}>
// // // //                 {rows.map((row) => {
// // // //                     prepareRow(row);
// // // //                     return (
// // // //                         <tr {...row.getRowProps()}>
// // // //                             {row.cells.map((cell) => {
// // // //                                 return <td {...cell.getCellProps()}>{cell.render("Cell")}</td>;
// // // //                             })}
// // // //                         </tr>
// // // //                     );
// // // //                 })}
// // // //             </tbody>
// // // //         </table>
// // // //     );
// // // // }

// // // // export default LoanRequest;
